import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MicIcon from "@mui/icons-material/Mic";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import { useEffect, useRef, useState } from "react";
import {
  DialogChatVoiceDraft,
  getSupportedDialogChatVoiceMimeType,
  stopDialogChatVoiceStream,
  validateDialogChatVoiceFile,
} from "../../../features/dialogChat/dialogChatVoiceMessages";
import strings from "../../../strings";
import AudioPlayer from "../../AudioPlayer/AudioPlayer";
import { TextChatAvailability } from "../TextChat.types";
import s from "./TextChatVoiceControl.module.css";

type TextChatVoiceControlProps = {
  availability: TextChatAvailability;
  onSend: (draft: DialogChatVoiceDraft) => Promise<void> | void;
  onActivityChange?: (isActive: boolean) => void;
};

const createVoiceDraft = (
  file: File,
  durationSeconds?: number,
): DialogChatVoiceDraft => ({
  id: `voice-${Date.now()}`,
  file,
  url: URL.createObjectURL(file),
  durationSeconds,
});

const TextChatVoiceControl = ({
  availability,
  onSend,
  onActivityChange,
}: TextChatVoiceControlProps) => {
  const startButtonRef = useRef<HTMLButtonElement | null>(null);
  const sendButtonRef = useRef<HTMLButtonElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number | null>(null);
  const draftRef = useRef<DialogChatVoiceDraft | null>(null);
  const sentDraftUrlsRef = useRef(new Set<string>());
  const [draft, setDraft] = useState<DialogChatVoiceDraft | null>(null);
  const [status, setStatus] = useState<
    "idle" | "recording" | "paused" | "preview" | "sending"
  >("idle");
  const [message, setMessage] = useState("");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const canUseVoice = availability.canWrite && status !== "sending";
  const isRecordingActive = status !== "idle";
  const shouldShowPanel = isRecordingActive || Boolean(message);
  const statusAnnouncement =
    message ||
    (status === "recording"
      ? strings.globalChatRecordingVoiceMessage
      : status === "paused"
        ? strings.globalChatVoiceRecordingPaused
        : status === "preview"
          ? strings.globalChatVoiceMessageReady
          : status === "sending"
            ? strings.sending
            : "");
  const formattedElapsedTime = `${Math.floor(elapsedSeconds / 60)
    .toString()
    .padStart(2, "0")}:${(elapsedSeconds % 60).toString().padStart(2, "0")}`;

  const getCurrentElapsedSeconds = () =>
    startedAtRef.current
      ? Math.max(0, Math.floor((Date.now() - startedAtRef.current) / 1000))
      : elapsedSeconds;

  const discardDraft = () => {
    if (draft?.url) {
      URL.revokeObjectURL(draft.url);
    }
    setDraft(null);
    setStatus("idle");
    setElapsedSeconds(0);
    startedAtRef.current = null;
    window.setTimeout(() => startButtonRef.current?.focus(), 0);
  };

  const stopStream = () => {
    stopDialogChatVoiceStream(streamRef.current);
    streamRef.current = null;
  };

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    onActivityChange?.(status !== "idle" || Boolean(draft));
  }, [draft, onActivityChange, status]);

  useEffect(() => {
    if (status === "preview") {
      sendButtonRef.current?.focus();
    }
  }, [status]);

  useEffect(() => {
    if (status !== "recording") {
      return;
    }

    const timer = window.setInterval(() => {
      setElapsedSeconds(getCurrentElapsedSeconds());
    }, 250);

    return () => window.clearInterval(timer);
  }, [status]);

  useEffect(
    () => () => {
      recorderRef.current?.stop();
      stopStream();
      const currentDraft = draftRef.current;
      if (
        currentDraft?.url &&
        !sentDraftUrlsRef.current.has(currentDraft.url)
      ) {
        URL.revokeObjectURL(currentDraft.url);
      }
    },
    [],
  );

  const startRecording = async () => {
    if (!canUseVoice || !navigator.mediaDevices?.getUserMedia) {
      setMessage(strings.globalChatVoicePermissionError);
      return;
    }

    try {
      setMessage("");
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedDialogChatVoiceMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });

      streamRef.current = stream;
      recorderRef.current = recorder;
      startedAtRef.current = Date.now();
      setElapsedSeconds(0);
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        recorderRef.current = null;
        const file = new File(chunksRef.current, "voice-message.webm", {
          type: mimeType,
        });
        const durationSeconds = Math.max(1, getCurrentElapsedSeconds());
        const validation = validateDialogChatVoiceFile(file, durationSeconds);

        stopStream();
        startedAtRef.current = null;
        setElapsedSeconds(durationSeconds);
        if (!validation.valid) {
          setMessage(strings.globalChatVoiceValidationError);
          setStatus("idle");
          return;
        }

        setDraft(createVoiceDraft(file, durationSeconds));
        setStatus("preview");
      };
      recorder.start();
      setStatus("recording");
      setMessage("");
    } catch {
      stopStream();
      setMessage(strings.globalChatVoicePermissionError);
      setStatus("idle");
    }
  };

  const pauseRecording = () => {
    recorderRef.current?.pause();
    setElapsedSeconds(getCurrentElapsedSeconds());
    startedAtRef.current = null;
    setStatus("paused");
  };

  const resumeRecording = () => {
    recorderRef.current?.resume();
    startedAtRef.current = Date.now() - elapsedSeconds * 1000;
    setStatus("recording");
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
  };

  const sendDraft = async () => {
    if (!draft) {
      return;
    }

    setStatus("sending");
    await onSend(draft);
    sentDraftUrlsRef.current.add(draft.url);
    setDraft(null);
    setStatus("idle");
    setElapsedSeconds(0);
    startedAtRef.current = null;
    window.setTimeout(() => startButtonRef.current?.focus(), 0);
  };

  return (
    <div
      className={`${s.container} ${isRecordingActive ? s.containerActive : ""}`}
    >
      {status === "idle" && (
        <button
          ref={startButtonRef}
          type="button"
          className={s.iconButton}
          aria-label={strings.globalChatStartVoiceRecording}
          disabled={!canUseVoice}
          onClick={startRecording}
        >
          <MicIcon fontSize="small" />
        </button>
      )}

      {shouldShowPanel && (
        <div className={s.recordingPanel}>
          <div className={s.recordingStatusRow}>
            <span
              className={`${s.recordingDot} ${
                status === "recording" ? s.recordingDotActive : ""
              }`}
              aria-hidden="true"
            />
            <div className={s.recordingTextBlock}>
              {statusAnnouncement && (
                <div
                  className={s.statusMessage}
                  role="status"
                  aria-live="polite"
                >
                  {statusAnnouncement}
                </div>
              )}
              {(status === "recording" || status === "paused") && (
                <span className={s.timer}>{formattedElapsedTime}</span>
              )}
            </div>
          </div>
          {draft && (
            <div
              className={s.preview}
              aria-label={strings.globalChatVoicePreview}
              data-prevent-reaction-picker="true"
            >
              <AudioPlayer url={draft.url} downloadIsAvailable={false} />
            </div>
          )}
          {isRecordingActive && (
            <div className={s.actions}>
              {status === "recording" && (
                <>
                  <button
                    type="button"
                    className={s.iconButton}
                    aria-label={strings.globalChatPauseVoiceRecording}
                    onClick={pauseRecording}
                  >
                    <PauseIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    className={s.iconButton}
                    aria-label={strings.globalChatStopVoiceRecording}
                    onClick={stopRecording}
                  >
                    <StopIcon fontSize="small" />
                  </button>
                </>
              )}
              {status === "paused" && (
                <>
                  <button
                    type="button"
                    className={s.iconButton}
                    aria-label={strings.globalChatResumeVoiceRecording}
                    onClick={resumeRecording}
                  >
                    <PlayArrowIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    className={s.iconButton}
                    aria-label={strings.globalChatStopVoiceRecording}
                    onClick={stopRecording}
                  >
                    <StopIcon fontSize="small" />
                  </button>
                </>
              )}
              {draft && status === "preview" && (
                <>
                  <button
                    type="button"
                    className={s.iconButton}
                    aria-label={strings.globalChatDiscardVoiceDraft}
                    onClick={discardDraft}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                  <button
                    ref={sendButtonRef}
                    type="button"
                    className={s.sendButton}
                    aria-label={strings.globalChatSendVoiceMessage}
                    onClick={sendDraft}
                  >
                    <SendIcon fontSize="small" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TextChatVoiceControl;
